/** SUNTRONIX ONE — Enterprise OS Home.
 *  GOOD MORNING → KPI summary → SUNTRONIX AI → MY ACTION CENTRE → BUSINESS PERFORMANCE. */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { HomeHeader, KpiStrip, AiBar, ActionCentrePanel, PerformancePanel, SectionLabel } from "./homeParts";
import { WhatsAppCommandCentre } from "./WhatsAppCommandCentre";
import StartMyDay from "./StartMyDay";
import { MyWorkToday } from "./MyWorkToday";
import { QuickActions } from "./QuickActions";
import { CustomerActionsPanel } from "@/pages/customer/CustomerActions";

const SMD_KEY = "st.os.smd.shown";

export default function EnterpriseHome() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [smdOpen, setSmdOpen] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/enterprise-os/home");
      setData(data); setRefreshedAt(new Date());
    } catch { if (!silent) toast.error("Could not load your home"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(() => load(true), 60000);
    return () => clearInterval(t);
  }, [load]);

  // Auto-offer the morning brief once per day (before noon).
  useEffect(() => {
    if (!data) return;
    const today = new Date().toISOString().slice(0, 10);
    let shown = null;
    try { shown = window.localStorage.getItem(SMD_KEY); } catch { /* ignore */ }
    if (shown !== today && new Date().getHours() < 12 && (data.brief?.total || 0) > 0) {
      setSmdOpen(true);
      try { window.localStorage.setItem(SMD_KEY, today); } catch { /* ignore */ }
    }
  }, [data]);

  const runAction = async (item) => {
    const a = item.actions?.[0];
    if (!a) { if (item.route) navigate(item.route); return; }
    if (a.kind === "route") { navigate(a.route); return; }
    try {
      await api.request({ method: a.api?.method || "POST", url: a.api?.url || a.url, data: a.api?.body || a.body });
      toast.success("Done"); load(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  const complete = async (item) => {
    try { await api.post("/one-today/complete", { key: item.key }); toast.success("Marked done"); load(true); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not complete"); }
  };

  const total = data?.brief?.total || 0;
  return (
    <div className="max-w-[1400px] mx-auto space-y-6 pb-8" data-testid="enterprise-home">
      <HomeHeader user={data?.user} total={total} onStart={() => setSmdOpen(true)} refreshedAt={refreshedAt} onRefresh={() => load(true)} />
      <QuickActions />
      <div className="grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)] gap-6 items-start">
        <MyWorkToday />
        <div className="space-y-4">
          <KpiStrip kpis={(data?.kpis || []).slice(0, 3)} loading={loading && !data} compact />
          <ActionCentrePanel ac={data?.action_centre} loading={loading && !data} onAction={runAction} onComplete={complete} />
        </div>
      </div>
      <CustomerActionsPanel internal limit={5} />
      <details className="os-card p-4" data-testid="home-more-tools">
        <summary className="cursor-pointer text-[13px] font-medium text-[color:var(--st-text)]">More: AI assistant, WhatsApp command centre, business performance</summary>
        <div className="mt-4 space-y-6">
          <AiBar />
          <WhatsAppCommandCentre />
          <section>
            <SectionLabel step="Insight" title="Business performance" hint="Month to date · live" />
            <PerformancePanel groups={data?.performance || []} loading={loading && !data} />
          </section>
        </div>
      </details>
      <StartMyDay open={smdOpen} onOpenChange={setSmdOpen} brief={data?.brief} user={data?.user} onAction={runAction} onComplete={complete} />
    </div>
  );
}
